/**
 * NetworkTransactionService - Servicio de transacciones para operaciones de red
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/services
 *
 * @description
 * Maneja operaciones atómicas en la red canónica con:
 * - Optimistic updates (actualización inmediata del mapa)
 * - Rollback automático en caso de error
 * - Queue de operaciones pendientes
 * - Estado de sincronización
 *
 * @example
 * ```typescript
 * const transaction = networkTransactionService.beginTransaction('create-branch', {
 *   optimisticData: { nodes: [newNode], edges: [newEdge] }
 * });
 *
 * try {
 *   await canonicalNetworkApi.createNode(...);
 *   await canonicalNetworkApi.createEdge(...);
 *   transaction.commit();
 * } catch (error) {
 *   transaction.rollback();
 * }
 * ```
 */

import type { CanonicalNode, CanonicalEdge } from '../types';
import { mapEngine } from '../../map-engine/MapEngineService';
import type { GeoJSONSource } from 'maplibre-gl';

// ============================================================================
// TYPES
// ============================================================================

export type TransactionStatus = 'pending' | 'committed' | 'rolled-back' | 'failed';

export type OperationType =
  | 'create-branch'
  | 'split-edge'
  | 'move-node'
  | 'delete-node'
  | 'create-node'
  | 'create-edge'
  | 'update-node'
  | 'update-edge';

export interface TransactionSnapshot {
  /** Features antes de la operación */
  features: GeoJSON.Feature[];
}

export interface Transaction {
  /** ID único de la transacción */
  id: string;
  /** Tipo de operación */
  type: OperationType;
  /** Estado actual */
  status: TransactionStatus;
  /** Timestamp de inicio */
  startedAt: number;
  /** Timestamp de finalización */
  completedAt: number | null;
  /** Snapshot del estado antes de la operación (para rollback) */
  snapshot: TransactionSnapshot;
  /** Datos optimistas agregados al mapa */
  optimisticData: {
    nodes?: Partial<CanonicalNode>[];
    edges?: Partial<CanonicalEdge>[];
    removedNodeIds?: string[];
    removedEdgeIds?: string[];
  };
  /** Error si falló */
  error?: Error;
}

// ============================================================================
// SERVICE
// ============================================================================

class NetworkTransactionService {
  private transactions = new Map<string, Transaction>();
  private transactionIdCounter = 0;

  /**
   * Inicia una nueva transacción
   */
  beginTransaction(
    type: OperationType,
    optimisticData: Transaction['optimisticData'] = {}
  ): Transaction {
    const transactionId = `tx-${++this.transactionIdCounter}-${Date.now()}`;

    console.log('[NetworkTransactionService] Beginning transaction:', transactionId, type);

    // Capturar snapshot del estado actual del mapa
    const snapshot = this.captureSnapshot();

    // Crear transacción
    const transaction: Transaction = {
      id: transactionId,
      type,
      status: 'pending',
      startedAt: Date.now(),
      completedAt: null,
      snapshot,
      optimisticData,
    };

    this.transactions.set(transactionId, transaction);

    // Aplicar optimistic updates si hay datos
    if (Object.keys(optimisticData).length > 0) {
      this.applyOptimisticUpdates(transaction);
    }

    return transaction;
  }

  /**
   * Confirma una transacción (operación exitosa en backend)
   */
  commit(transactionId: string): void {
    const transaction = this.transactions.get(transactionId);
    if (!transaction) {
      console.warn('[NetworkTransactionService] Transaction not found:', transactionId);
      return;
    }

    console.log('[NetworkTransactionService] Committing transaction:', transactionId);

    transaction.status = 'committed';
    transaction.completedAt = Date.now();

    // No necesitamos hacer nada más - los optimistic updates ya están aplicados
    // y ahora sabemos que el backend los confirmó

    // Limpiar transacción después de un tiempo (para debugging)
    setTimeout(() => {
      this.transactions.delete(transactionId);
    }, 5000);
  }

  /**
   * Revierte una transacción (operación falló en backend)
   */
  rollback(transactionId: string, error?: Error): void {
    const transaction = this.transactions.get(transactionId);
    if (!transaction) {
      console.warn('[NetworkTransactionService] Transaction not found:', transactionId);
      return;
    }

    console.error('[NetworkTransactionService] Rolling back transaction:', transactionId, error);

    transaction.status = 'rolled-back';
    transaction.completedAt = Date.now();
    transaction.error = error;

    // Restaurar snapshot (revertir optimistic updates)
    this.restoreSnapshot(transaction.snapshot);

    // Limpiar transacción después de un tiempo
    setTimeout(() => {
      this.transactions.delete(transactionId);
    }, 5000);
  }

  /**
   * Obtiene el estado de una transacción
   */
  getTransaction(transactionId: string): Transaction | undefined {
    return this.transactions.get(transactionId);
  }

  /**
   * Obtiene todas las transacciones pendientes
   */
  getPendingTransactions(): Transaction[] {
    return Array.from(this.transactions.values()).filter((tx) => tx.status === 'pending');
  }

  /**
   * Verifica si hay transacciones pendientes
   */
  hasPendingTransactions(): boolean {
    return this.getPendingTransactions().length > 0;
  }

  // ──────────────────────────────────────────────────────────────
  // PRIVATE METHODS
  // ──────────────────────────────────────────────────────────────

  /**
   * Captura snapshot del estado actual del mapa
   */
  private captureSnapshot(): TransactionSnapshot {
    const map = mapEngine.getMap();
    if (!map) {
      return { features: [] };
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      return { features: [] };
    }

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) {
      return { features: [] };
    }

    // Clonar features (deep copy)
    return {
      features: JSON.parse(JSON.stringify(sourceData.features)),
    };
  }

  /**
   * Restaura snapshot (rollback)
   */
  private restoreSnapshot(snapshot: TransactionSnapshot): void {
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[NetworkTransactionService] Cannot restore: map not available');
      return;
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      console.warn('[NetworkTransactionService] Cannot restore: source not found');
      return;
    }

    // Restaurar features desde snapshot
    source.setData({
      type: 'FeatureCollection',
      features: snapshot.features,
    });

    console.log('[NetworkTransactionService] Snapshot restored');
  }

  /**
   * Aplica optimistic updates al mapa
   */
  private applyOptimisticUpdates(transaction: Transaction): void {
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[NetworkTransactionService] Cannot apply updates: map not available');
      return;
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      console.warn('[NetworkTransactionService] Cannot apply updates: source not found');
      return;
    }

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) {
      console.warn('[NetworkTransactionService] Cannot apply updates: invalid source data');
      return;
    }

    let features = [...sourceData.features];

    // Remover nodos/edges marcados para eliminación
    if (transaction.optimisticData.removedNodeIds) {
      features = features.filter(
        (f) => !transaction.optimisticData.removedNodeIds!.includes(f.id as string)
      );
    }
    if (transaction.optimisticData.removedEdgeIds) {
      features = features.filter(
        (f) => !transaction.optimisticData.removedEdgeIds!.includes(f.id as string)
      );
    }

    // Agregar nuevos nodos
    if (transaction.optimisticData.nodes) {
      transaction.optimisticData.nodes.forEach((node) => {
        features.push({
          type: 'Feature',
          id: node.id,
          geometry: node.geometry!,
          properties: {
            id: node.id, // Requerido para lookup
            type: 'node',
            intersectionType: node.intersectionType || null,
            _optimistic: true, // Marcar como optimistic
            _transactionId: transaction.id,
          },
        });
      });
    }

    // Agregar nuevos edges
    if (transaction.optimisticData.edges) {
      transaction.optimisticData.edges.forEach((edge) => {
        features.push({
          type: 'Feature',
          id: edge.id,
          geometry: edge.geometry!,
          properties: {
            id: edge.id, // Para consistencia
            type: 'edge',
            roadClass: edge.roadClass || null,
            fromNodeId: (edge as any).fromNodeId || edge.fromNode?.id,
            toNodeId: (edge as any).toNodeId || edge.toNode?.id,
            lengthMeters: edge.lengthMeters || 0,
            _optimistic: true, // Marcar como optimistic
            _transactionId: transaction.id,
          },
        });
      });
    }

    // Actualizar source
    source.setData({
      type: 'FeatureCollection',
      features,
    });

    console.log('[NetworkTransactionService] Optimistic updates applied:', {
      nodes: transaction.optimisticData.nodes?.length || 0,
      edges: transaction.optimisticData.edges?.length || 0,
      removedNodes: transaction.optimisticData.removedNodeIds?.length || 0,
      removedEdges: transaction.optimisticData.removedEdgeIds?.length || 0,
    });
  }
}

// Exportar singleton
export const networkTransactionService = new NetworkTransactionService();
